import { useCallback } from 'react';
import { Shape, Tool } from '../types/Shape';
import { findNearestConnectionPoint, findShapeAtPoint, getRelativePosition } from '../utils/connectionUtils';
import { updateArrowPosition, updateArrowConnections } from '../utils/arrowUtils';

export interface UseArrowHandlersProps {
  shapes: Shape[];
  setShapes: React.Dispatch<React.SetStateAction<Shape[]>>;
  stageScale: number;
  currentShape: Partial<Shape> | null;
  setCurrentShape: React.Dispatch<React.SetStateAction<Partial<Shape> | null>>;
  isConnecting: boolean;
  setIsConnecting: React.Dispatch<React.SetStateAction<boolean>>;
  setShowConnectionPoints: React.Dispatch<React.SetStateAction<boolean>>;
  setSnappedPoint: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  setCurrentTool: React.Dispatch<React.SetStateAction<Tool>>;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  defaultArrowStroke: string;
  defaultArrowStrokeWidth: number;
  defaultStartArrow: boolean;
  defaultEndArrow: boolean;
  currentTool: Tool;
  // Arrow drag handlers
  setContextMenu: React.Dispatch<React.SetStateAction<{ x: number; y: number; shapeId: string } | null>>;
  setIsDraggingArrowEndpoint: React.Dispatch<React.SetStateAction<boolean>>;
  pushHistory: (shapes: Shape[]) => void;
  hasActionStarted: React.MutableRefObject<boolean>;
}

export interface UseArrowHandlersReturn {
  handleArrowStageClick: (canvasX: number, canvasY: number) => void;
  handleStartPointMove: (arrowId: string, newX: number, newY: number) => void;
  handleEndPointMove: (arrowId: string, newX: number, newY: number) => void;
  handleStartPointEnd: (arrowId: string, finalX: number, finalY: number) => void;
  handleEndPointEnd: (arrowId: string, finalX: number, finalY: number) => void;
  handleArrowContextMenu: (shapeId: string, e: React.MouseEvent) => void;
  handleArrowDragStart: () => void;
  handleArrowDragMove: (shapeId: string, dx: number, dy: number) => void;
  handleArrowDragEnd: () => void;
  handleSnapUpdate: (snappedPoint: { x: number; y: number } | null) => void;
  handleEndpointDraggingChange: (isDragging: boolean) => void;
  handleCurveControlMove: (arrowId: string, offset: { x: number; y: number }) => void;
  handleWaypointMove: (arrowId: string, waypointIndex: number, x: number, y: number) => void;
  handleWaypointAdd: (arrowId: string, x: number, y: number, insertIndex: number) => void;
  handleWaypointDelete: (arrowId: string, waypointIndex: number) => void;
}

export function useArrowHandlers({
  shapes,
  setShapes,
  stageScale,
  currentShape,
  setCurrentShape,
  isConnecting,
  setIsConnecting,
  setShowConnectionPoints,
  setSnappedPoint,
  setCurrentTool,
  setHasUnsavedChanges,
  defaultArrowStroke,
  defaultArrowStrokeWidth,
  defaultStartArrow,
  defaultEndArrow,
  currentTool,
  setContextMenu,
  setIsDraggingArrowEndpoint,
  pushHistory,
  hasActionStarted,
}: UseArrowHandlersProps): UseArrowHandlersReturn {

  // Handle stage click for arrow drawing
  const handleArrowStageClick = useCallback((canvasX: number, canvasY: number) => {
    // 화살표 그리기 완료
    if (isConnecting && currentShape && currentShape.type === 'arrow') {
      // 끝점 스냅
      const nearestPoint = findNearestConnectionPoint(canvasX, canvasY, shapes, 20 / stageScale);

      let endX = canvasX;
      let endY = canvasY;
      let endShapeId: string | undefined;
      let endConnectionPoint: 'top' | 'right' | 'bottom' | 'left' | undefined;
      let endRelativeX: number | undefined;
      let endRelativeY: number | undefined;

      if (nearestPoint) {
        endX = nearestPoint.x;
        endY = nearestPoint.y;
        endShapeId = nearestPoint.shapeId;
        endConnectionPoint = nearestPoint.pointType;
      } else {
        const shapeAtPoint = findShapeAtPoint(canvasX, canvasY, shapes.filter(s => s.type !== 'arrow'));
        if (shapeAtPoint) {
          endShapeId = shapeAtPoint.id;
          const relativePos = getRelativePosition(canvasX, canvasY, shapeAtPoint);
          if (relativePos) {
            endRelativeX = relativePos.relativeX;
            endRelativeY = relativePos.relativeY;
          }
        }
      }

      // 최소 거리 체크
      const arrowWidth = endX - (currentShape.x || 0);
      const arrowHeight = endY - (currentShape.y || 0);
      const distance = Math.sqrt(arrowWidth * arrowWidth + arrowHeight * arrowHeight);

      if (distance >= 5) {
        const completedArrow: Shape = {
          ...currentShape as Shape,
          id: Date.now().toString(),
          width: arrowWidth,
          height: arrowHeight,
          endShapeId,
          endConnectionPoint,
          endRelativeX,
          endRelativeY,
        };

        setShapes(prev => {
          const newShapes = [...prev, completedArrow];

          // connectedArrows 업데이트
          if (completedArrow.startShapeId) {
            newShapes.forEach(shape => {
              if (shape.id === completedArrow.startShapeId) {
                if (!shape.connectedArrows) {
                  shape.connectedArrows = [];
                }
                shape.connectedArrows.push(completedArrow.id);
              }
            });
          }

          if (completedArrow.endShapeId) {
            newShapes.forEach(shape => {
              if (shape.id === completedArrow.endShapeId) {
                if (!shape.connectedArrows) {
                  shape.connectedArrows = [];
                }
                shape.connectedArrows.push(completedArrow.id);
              }
            });
          }

          return newShapes;
        });
      }

      // 상태 초기화
      setCurrentShape(null);
      setIsConnecting(false);
      setShowConnectionPoints(false);
      setSnappedPoint(null);
      setCurrentTool('select');
      return;
    }

    // 화살표 도구일 때 HTML shape 클릭 시 시작점으로 처리
    if (currentTool === 'arrow' && !isConnecting) {
      // 연결점 스냅
      const nearestPoint = findNearestConnectionPoint(canvasX, canvasY, shapes, 20 / stageScale);

      let startX = canvasX;
      let startY = canvasY;
      let startShapeId: string | undefined;
      let startConnectionPoint: 'top' | 'right' | 'bottom' | 'left' | undefined;
      let startRelativeX: number | undefined;
      let startRelativeY: number | undefined;

      if (nearestPoint) {
        startX = nearestPoint.x;
        startY = nearestPoint.y;
        startShapeId = nearestPoint.shapeId;
        startConnectionPoint = nearestPoint.pointType;
      } else {
        const shapeAtPoint = findShapeAtPoint(canvasX, canvasY, shapes);
        if (shapeAtPoint) {
          startShapeId = shapeAtPoint.id;
          const relativePos = getRelativePosition(canvasX, canvasY, shapeAtPoint);
          if (relativePos) {
            startRelativeX = relativePos.relativeX;
            startRelativeY = relativePos.relativeY;
          }
        }
      }

      // 화살표는 항상 최상위 + 짝수 order
      const maxOrder = Math.max(...shapes.map(s => s.order ?? 0), 0);
      const newOrder = maxOrder % 2 === 0 ? maxOrder + 2 : maxOrder + 1;

      // 화살표 그리기 시작
      setIsConnecting(true);
      setShowConnectionPoints(true);
      setCurrentShape({
        id: 'temp-arrow',
        type: 'arrow',
        x: startX,
        y: startY,
        width: 0,
        height: 0,
        stroke: defaultArrowStroke,
        strokeWidth: defaultArrowStrokeWidth,
        isCurved: false,
        startShapeId,
        startConnectionPoint,
        startRelativeX,
        startRelativeY,
        startArrow: defaultStartArrow,
        endArrow: defaultEndArrow,
        order: newOrder,
        renderMode: 'html',
      });
    }
  }, [shapes, stageScale, currentShape, isConnecting, currentTool, defaultArrowStroke, defaultArrowStrokeWidth, defaultStartArrow, defaultEndArrow, setShapes, setCurrentShape, setIsConnecting, setShowConnectionPoints, setSnappedPoint, setCurrentTool]);

  // Handle start point move during arrow endpoint drag
  const handleStartPointMove = useCallback((arrowId: string, newX: number, newY: number) => {
    setShapes(prev => prev.map(s => {
      if (s.id === arrowId && s.type === 'arrow') {
        // 끝점 고정: 원래 끝점 = (x + width, y + height)
        const endX = s.x + (s.width || 0);
        const endY = s.y + (s.height || 0);
        return {
          ...s,
          x: newX,
          y: newY,
          // 끝점을 고정하기 위해 width, height 재계산
          width: endX - newX,
          height: endY - newY,
          startRelativeX: undefined,
          startRelativeY: undefined
        };
      }
      return s;
    }));
  }, [setShapes]);

  // Handle end point move during arrow endpoint drag
  const handleEndPointMove = useCallback((arrowId: string, newX: number, newY: number) => {
    setShapes(prev => prev.map(s => {
      if (s.id === arrowId && s.type === 'arrow') {
        return {
          ...s,
          width: newX - s.x,
          height: newY - s.y,
          endRelativeX: undefined,
          endRelativeY: undefined
        };
      }
      return s;
    }));
  }, [setShapes]);

  // Handle start point end (snap to connection points)
  const handleStartPointEnd = useCallback((arrowId: string, finalX: number, finalY: number) => {
    setShapes(prev => {
      const arrow = prev.find(s => s.id === arrowId);
      if (!arrow || arrow.type !== 'arrow') return prev;

      const oldStartShapeId = arrow.startShapeId;
      let newStartShapeId: string | undefined;
      let newStartConnectionPoint: 'top' | 'right' | 'bottom' | 'left' | undefined;
      let newStartRelativeX: number | undefined;
      let newStartRelativeY: number | undefined;

      // 연결점 스냅 확인
      const nearestPoint = findNearestConnectionPoint(finalX, finalY, prev.filter(s => s.id !== arrowId), 20 / stageScale);

      if (nearestPoint) {
        newStartShapeId = nearestPoint.shapeId;
        newStartConnectionPoint = nearestPoint.pointType;
      } else {
        // 객체 위에 있는지 확인
        const shapeAtPoint = findShapeAtPoint(finalX, finalY, prev.filter(s => s.type !== 'arrow'));
        if (shapeAtPoint) {
          newStartShapeId = shapeAtPoint.id;
          const relativePos = getRelativePosition(finalX, finalY, shapeAtPoint);
          if (relativePos) {
            newStartRelativeX = relativePos.relativeX;
            newStartRelativeY = relativePos.relativeY;
          }
        }
      }

      // 끝점 고정: 원래 끝점 = (x + width, y + height)
      const endX = arrow.x + (arrow.width || 0);
      const endY = arrow.y + (arrow.height || 0);

      // arrow 업데이트
      const updatedArrow = {
        ...arrow,
        x: finalX,
        y: finalY,
        // 끝점을 고정하기 위해 width, height 재계산
        width: endX - finalX,
        height: endY - finalY,
        startShapeId: newStartShapeId,
        startConnectionPoint: newStartConnectionPoint,
        startRelativeX: newStartRelativeX,
        startRelativeY: newStartRelativeY,
        startArrow: arrow.startArrow
      };

      // 위치 보정
      const startShape = prev.find(s => s.id === newStartShapeId);
      const finalArrow = newStartShapeId && startShape
        ? updateArrowPosition(updatedArrow, startShape, newStartShapeId)
        : updatedArrow;

      // connectedArrows 업데이트
      const updatedShapes = updateArrowConnections(
        prev,
        arrowId,
        oldStartShapeId,
        arrow.endShapeId,
        newStartShapeId,
        arrow.endShapeId
      );

      // 최종 리스트에서 arrow 교체
      return updatedShapes.map(s => s.id === arrowId ? finalArrow : s);
    });
    setHasUnsavedChanges(true);
  }, [stageScale, setShapes, setHasUnsavedChanges]);

  // Handle end point end (snap to connection points)
  const handleEndPointEnd = useCallback((arrowId: string, finalX: number, finalY: number) => {
    setShapes(prev => {
      const arrow = prev.find(s => s.id === arrowId);
      if (!arrow || arrow.type !== 'arrow') return prev;

      const oldEndShapeId = arrow.endShapeId;
      let newEndShapeId: string | undefined;
      let newEndConnectionPoint: 'top' | 'right' | 'bottom' | 'left' | undefined;
      let newEndRelativeX: number | undefined;
      let newEndRelativeY: number | undefined;

      const nearestPoint = findNearestConnectionPoint(finalX, finalY, prev.filter(s => s.id !== arrowId), 20 / stageScale);

      if (nearestPoint) {
        newEndShapeId = nearestPoint.shapeId;
        newEndConnectionPoint = nearestPoint.pointType;
      } else {
        const shapeAtPoint = findShapeAtPoint(finalX, finalY, prev.filter(s => s.type !== 'arrow'));
        if (shapeAtPoint) {
          newEndShapeId = shapeAtPoint.id;
          const relativePos = getRelativePosition(finalX, finalY, shapeAtPoint);
          if (relativePos) {
            newEndRelativeX = relativePos.relativeX;
            newEndRelativeY = relativePos.relativeY;
          }
        }
      }

      const updatedArrow = {
        ...arrow,
        width: finalX - arrow.x,
        height: finalY - arrow.y,
        endShapeId: newEndShapeId,
        endConnectionPoint: newEndConnectionPoint,
        endRelativeX: newEndRelativeX,
        endRelativeY: newEndRelativeY,
        endArrow: arrow.endArrow
      };

      const endShape = prev.find(s => s.id === newEndShapeId);
      const finalArrow = newEndShapeId && endShape
        ? updateArrowPosition(updatedArrow, endShape, newEndShapeId)
        : updatedArrow;

      const updatedShapes = updateArrowConnections(
        prev,
        arrowId,
        arrow.startShapeId,
        oldEndShapeId,
        arrow.startShapeId,
        newEndShapeId
      );

      return updatedShapes.map(s => s.id === arrowId ? finalArrow : s);
    });
    setHasUnsavedChanges(true);
  }, [stageScale, setShapes, setHasUnsavedChanges]);

  // Handle arrow context menu
  const handleArrowContextMenu = useCallback((shapeId: string, e: React.MouseEvent) => {
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      shapeId,
    });
  }, [setContextMenu]);

  // Handle arrow drag start
  const handleArrowDragStart = useCallback(() => {
    if (!hasActionStarted.current) {
      pushHistory(shapes);
      hasActionStarted.current = true;
    }
  }, [shapes, pushHistory, hasActionStarted]);

  // Handle arrow drag move
  const handleArrowDragMove = useCallback((shapeId: string, dx: number, dy: number) => {
    setShapes(prev => {
      const arrow = prev.find(s => s.id === shapeId);
      if (!arrow) return prev;

      // 연결된 도형들의 connectedArrows에서 이 화살표 제거
      const startShapeId = arrow.startShapeId;
      const endShapeId = arrow.endShapeId;

      return prev.map(s => {
        if (s.id === shapeId) {
          // 화살표: 위치 이동 + 웨이포인트도 함께 이동 + 연결 정보 제거
          return {
            ...s,
            x: s.x + dx,
            y: s.y + dy,
            // 웨이포인트도 함께 이동
            waypoints: s.waypoints?.map(wp => ({
              x: wp.x + dx,
              y: wp.y + dy,
            })),
            startShapeId: undefined,
            endShapeId: undefined,
            startConnectionPoint: undefined,
            endConnectionPoint: undefined,
            startRelativeX: undefined,
            startRelativeY: undefined,
            endRelativeX: undefined,
            endRelativeY: undefined,
          };
        }
        // 연결된 도형들의 connectedArrows에서 화살표 제거
        if ((s.id === startShapeId || s.id === endShapeId) && s.connectedArrows) {
          return {
            ...s,
            connectedArrows: s.connectedArrows.filter(id => id !== shapeId),
          };
        }
        return s;
      });
    });
  }, [setShapes]);

  // Handle arrow drag end
  const handleArrowDragEnd = useCallback(() => {
    hasActionStarted.current = false;
    setHasUnsavedChanges(true);
  }, [hasActionStarted, setHasUnsavedChanges]);

  // Handle snap update
  const handleSnapUpdate = useCallback((snappedPoint: { x: number; y: number } | null) => {
    setSnappedPoint(snappedPoint);
  }, [setSnappedPoint]);

  // Handle endpoint dragging change
  const handleEndpointDraggingChange = useCallback((isDragging: boolean) => {
    setIsDraggingArrowEndpoint(isDragging);
  }, [setIsDraggingArrowEndpoint]);

  // Handle curve control point move
  const handleCurveControlMove = useCallback((arrowId: string, offset: { x: number; y: number }) => {
    setShapes(prev => prev.map(s => {
      if (s.id === arrowId && s.type === 'arrow') {
        return {
          ...s,
          curveOffset: offset,
          // curvature는 더 이상 사용하지 않음 (curveOffset으로 대체)
          curvature: undefined,
        };
      }
      return s;
    }));
    setHasUnsavedChanges(true);
  }, [setShapes, setHasUnsavedChanges]);

  // Handle waypoint move
  const handleWaypointMove = useCallback((arrowId: string, waypointIndex: number, x: number, y: number) => {
    setShapes(prev => prev.map(s => {
      if (s.id === arrowId && s.type === 'arrow' && s.waypoints) {
        const newWaypoints = [...s.waypoints];
        newWaypoints[waypointIndex] = { x, y };
        return {
          ...s,
          waypoints: newWaypoints,
        };
      }
      return s;
    }));
  }, [setShapes]);

  // Handle waypoint add
  const handleWaypointAdd = useCallback((arrowId: string, x: number, y: number, insertIndex: number) => {
    setShapes(prev => prev.map(s => {
      if (s.id === arrowId && s.type === 'arrow') {
        const currentWaypoints = s.waypoints || [];
        const newWaypoints = [
          ...currentWaypoints.slice(0, insertIndex),
          { x, y },
          ...currentWaypoints.slice(insertIndex),
        ];
        return {
          ...s,
          waypoints: newWaypoints,
        };
      }
      return s;
    }));
    setHasUnsavedChanges(true);
  }, [setShapes, setHasUnsavedChanges]);

  // Handle waypoint delete
  const handleWaypointDelete = useCallback((arrowId: string, waypointIndex: number) => {
    setShapes(prev => prev.map(s => {
      if (s.id === arrowId && s.type === 'arrow' && s.waypoints) {
        const newWaypoints = s.waypoints.filter((_, i) => i !== waypointIndex);
        return {
          ...s,
          waypoints: newWaypoints.length > 0 ? newWaypoints : undefined,
        };
      }
      return s;
    }));
    setHasUnsavedChanges(true);
  }, [setShapes, setHasUnsavedChanges]);

  return {
    handleArrowStageClick,
    handleStartPointMove,
    handleEndPointMove,
    handleStartPointEnd,
    handleEndPointEnd,
    handleArrowContextMenu,
    handleArrowDragStart,
    handleArrowDragMove,
    handleArrowDragEnd,
    handleSnapUpdate,
    handleEndpointDraggingChange,
    handleCurveControlMove,
    handleWaypointMove,
    handleWaypointAdd,
    handleWaypointDelete,
  };
}
